import { Box, Divider, Typography } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import Theme from "../utils/theme";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import axiosService from "../utils/axios";

type StarRatingProps = {
    objectId: string;
    count: number;
    average: number;
    value: number;
    onChange?: (value: number) => void;
    max?: number;
    disabled?: boolean;
    StarComponent?: React.ComponentType<{ filled: boolean; size: number }>;
    size?: "sm" | "md" | "lg";
    hoverable?: boolean;
};

export const StarRating: React.FC<StarRatingProps> = ({
    objectId,
    average,
    count,
    value,
    onChange,
    max = 5,
    disabled = false,
    StarComponent,
    size = "md",
    hoverable = false,
}) => {
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const [hoverValue, setHoverValue] = useState<number | null>(null);

    const starSize = size === "md" ? 18 : size === "lg" ? 20 : 16;
    const fontSize = size === "md" ? "17px" : size === "lg" ? "18px" : "16px";

    const effectiveValue = hoverable ? hoverValue ?? value : Math.floor(average);

    const handleClick = useCallback(
        (index: number) => {
            if (disabled || !onChange) return;
            onChange(index + 1);

            axiosService.post(`${process.env.REACT_APP_API_URL}/recipes/update/ranking`, { recipeId: objectId, value: index + 1 });
        },
        [disabled, objectId, onChange]
    );

    const handleMouseEnter = useCallback(
        (index: number) => {
            if (disabled || !hoverable) return;
            setHoverValue(index + 1);
        },
        [disabled, hoverable]
    );

    const handleMouseLeaveAll = useCallback(() => {
        if (disabled || !hoverable) return;
        setHoverValue(null);
    }, [disabled, hoverable]);

    const DefaultStar: React.FC<{ filled: boolean; size: number }> = ({ filled, size }) => (
        <Box
            sx={{
                width: size,
                height: size,
                padding: 0,
                borderRadius: 0,
                transition: "transform 0.15s ease, filter 0.15s ease",
                filter: filled ? "drop-shadow(0 0 4px rgba(0,0,0,0.15))" : "none",
                transform: filled ? "scale(1.05)" : "scale(1)",
                color: value !== 0 && filled ? theme.palette.success.main : theme.palette.primary.main,
                fontWeight: "bold",
                textAlign: "center",
                "&:hover": {
                    transform: "scale(1.1)",
                },
            }}
        >
            <Box
                component="span"
                sx={{
                    fontSize: { fontSize },
                    userSelect: "none",
                    lineHeight: 1,
                }}
            >
                {filled ? "★" : "☆"}
            </Box>
        </Box>
    );

    const StaticStar: React.FC<{ filled: boolean; size: number }> = ({ filled, size }) => (
        <Box
            sx={{
                width: size,
                height: size,
                padding: 0,
                borderRadius: 0,
                transition: "transform 0.15s ease, filter 0.15s ease",
                filter: filled ? "drop-shadow(0 0 4px rgba(0,0,0,0.15))" : "none",
                transform: filled ? "scale(1.05)" : "scale(1)",
                color: theme.palette.primary.main,
                fontWeight: "bold",
                textAlign: "center",
            }}
        >
            <Box
                component="span"
                sx={{
                    fontSize: size * 0.9,
                    userSelect: "none",
                    lineHeight: 1,
                }}
            >
                {filled ? "★" : "☆"}
            </Box>
        </Box>
    );

    const Star = StarComponent ?? (hoverable ? DefaultStar : StaticStar);

    return (
        <Box
            sx={{
                display: "inline-flex",
                gap: 0.5,
                padding: 0.5,
                borderRadius: "999px",
                background: "rgba(0,0,0,0.04)",
                justifyContent: "center",
                alignItems: "center",
            }}
            onMouseLeave={handleMouseLeaveAll}
        >
            {Array.from({ length: max }).map((_, index) => {
                const filled = index < effectiveValue;

                return (
                    <Box
                        key={index}
                        onClick={() => handleClick(index)}
                        onMouseEnter={() => handleMouseEnter(index)} // only enter on stars
                        sx={{
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            margin: 0,
                            cursor: disabled ? "default" : "pointer",
                        }}
                        aria-label={`Set rating to ${index + 1}`}
                    >
                        <Box>
                            <Star filled={filled} size={starSize} />
                        </Box>
                    </Box>
                );
            })}
            <Divider
                orientation="vertical"
                flexItem
                sx={{
                    mx: 0.5,
                    opacity: 1,
                    borderColor: theme.palette.primary.main,
                }}
            />

            <Typography
                variant="subtitle2"
                sx={{
                    width: "100%",
                    position: "relative",
                    bottom: "2px",
                    display: "flex",
                    alignItems: "center",
                    lineHeight: 1,
                    fontSize: { fontSize },
                    color: theme.palette.primary.main,
                }}
            >
                {average !== 0 ? average.toFixed(1) : ""} ({count ?? 0})
            </Typography>
        </Box>
    );
};
